import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  fetchWeeklyReportCreatorDelta,
  fetchWeeklyReportCreatorPage,
  fetchWeeklyReportCreatorSnapshot,
  WeeklyReportCreatorFetchArgs,
  WeeklyReportCreatorOption,
  WeeklyReportCreatorPage,
} from '../api/weeklySearch';
import {
  areCreatorOptionsCacheCriteriaEqual,
  buildCreatorOptionsIndexedDbKey,
  createCreatorOptionsCacheCriteria,
  CREATOR_PREFETCH_FRESH_MS,
  CREATOR_SYNC_MIN_REVALIDATE_MS,
  CreatorOptionsCacheRecord,
  readCreatorOptionsCache,
  subscribeCreatorOptionsCacheUpdates,
  synchronizeCreatorOptionPages,
  withCreatorOptionsFetchLock,
} from '../lib/creatorOptionsIndexedDb';
import {
  CREATOR_SEARCH_DEBOUNCE_MS,
  CREATOR_SEARCH_REQUEST_PAGE_SIZES,
  CREATOR_SEARCH_RESULT_LIMIT,
} from '../constants/creatorOptions';

const normalizeSearchText = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFKC')
    .replace(/\s+/g, '')
    .toLocaleLowerCase();

const isAbortError = (error: unknown): boolean =>
  (error as any)?.name === 'AbortError' ||
  (error as any)?.name === 'CanceledError' ||
  (error as any)?.code === 'ERR_CANCELED';

const CREATOR_PREFETCH_DELAY_MS = 2000;
const CREATOR_PREFETCH_MAX_RANGE_DAYS = 14;

const parseYmdUtc = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatYmdUtc = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate()
  ).padStart(2, '0')}`;

const addUtcDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

export const buildAdjacentCreatorRanges = (
  startDate: string,
  endDate: string
): Array<{ startDate: string; endDate: string }> => {
  const start = parseYmdUtc(startDate);
  const end = parseYmdUtc(endDate);
  if (!start || !end || start > end) return [];
  const rangeDays = Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1;
  if (rangeDays < 1 || rangeDays > CREATOR_PREFETCH_MAX_RANGE_DAYS) return [];
  const previousEnd = addUtcDays(start, -1);
  const previousStart = addUtcDays(previousEnd, -(rangeDays - 1));
  const nextStart = addUtcDays(end, 1);
  const nextEnd = addUtcDays(nextStart, rangeDays - 1);
  return [
    { startDate: formatYmdUtc(previousStart), endDate: formatYmdUtc(previousEnd) },
    { startDate: formatYmdUtc(nextStart), endDate: formatYmdUtc(nextEnd) },
  ];
};

export const replaceCreatorCriteriaDateRange = (
  criteriaKey: string,
  startDate: string,
  endDate: string
): string => {
  const parts = criteriaKey.split('|');
  if (parts.length < 2) return criteriaKey;
  return [startDate, endDate, ...parts.slice(2)].join('|');
};

/**
 * ページAPIの初回だけ、後方互換の全件APIへフォールバックする。
 * 2ページ目以降の失敗は部分キャッシュのカーソルを保持して再試行する。
 */
export async function fetchCreatorPageWithSnapshotFallback(
  args: WeeklyReportCreatorFetchArgs,
  pageCursor: string | null,
  pageSize: number
): Promise<WeeklyReportCreatorPage> {
  try {
    const page = await fetchWeeklyReportCreatorPage(args, { pageCursor, pageSize });

    // 日付のみなど候補数が多い条件で、ページAPIが誤って空を返す旧環境にも対応する。
    // 全件APIにも候補がなければ、ページAPIの正常な0件結果をそのまま採用する。
    if (!pageCursor && !page.hasMore && page.items.length === 0) {
      try {
        const snapshot = await fetchWeeklyReportCreatorSnapshot(args);
        if (snapshot.options.length > 0) {
          return {
            items: snapshot.options,
            nextPageCursor: null,
            hasMore: false,
            syncCursor: null,
          };
        }
      } catch (fallbackError) {
        if (isAbortError(fallbackError)) throw fallbackError;
      }
    }

    return page;
  } catch (error) {
    if (isAbortError(error) || pageCursor) throw error;

    const snapshot = await fetchWeeklyReportCreatorSnapshot(args);
    return {
      items: snapshot.options,
      nextPageCursor: null,
      hasMore: false,
      // ページAPIが利用できない環境では差分APIも前提にせず、次回再検証する。
      syncCursor: null,
    };
  }
}

const waitUntilSearchIdle = async (
  searchActiveRef: MutableRefObject<boolean>,
  signal: AbortSignal
): Promise<void> => {
  while (searchActiveRef.current && !signal.aborted) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
  }
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
};

export interface ProgressiveCreatorOptionsArgs {
  enabled: boolean;
  criteriaKey: string | null;
  loginId: string | null;
  startDate: string;
  endDate: string;
  departmentIds: string[];
  teamIds: string[];
  includeTemporary?: boolean;
  userDep?: string;
  userTeamId?: string | number;
  revalidateToken: number;
  forceRevalidateRef: MutableRefObject<boolean>;
  latestUpdateMs: number;
  legacyCacheKey?: string | null;
  legacyCacheVersion?: number;
  setOptions: Dispatch<SetStateAction<WeeklyReportCreatorOption[] | null>>;
  setLoadingRemaining: Dispatch<SetStateAction<boolean>>;
  setResolvedKey: Dispatch<SetStateAction<string | null>>;
}

export interface ProgressiveCreatorOptionsResult {
  remoteSearchOptions: WeeklyReportCreatorOption[];
  searchLoading: boolean;
  searchResultsComplete: boolean;
  searchError: string | null;
  /** 候補一覧の取得に失敗し、利用できる完成済み候補が無い場合の案内文。 */
  loadErrorMessage: string | null;
  allOptionsLoaded: boolean;
  loadingText: string;
  onSearchTextChange: (value: string) => void;
  retry: () => void;
}

/** 両検索画面で共用する、発行者のページ取得・差分同期・API検索。 */
export function useProgressiveCreatorOptions(
  args: ProgressiveCreatorOptionsArgs
): ProgressiveCreatorOptionsResult {
  const [retryToken, setRetryToken] = useState(0);
  const [searchRequest, setSearchRequest] = useState({ text: '', token: 0 });
  const searchText = searchRequest.text;
  const [remoteSearchOptions, setRemoteSearchOptions] = useState<WeeklyReportCreatorOption[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResultsComplete, setSearchResultsComplete] = useState(true);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [allOptionsLoaded, setAllOptionsLoaded] = useState(false);
  const [refreshingCachedOptions, setRefreshingCachedOptions] = useState(false);
  const [prefetchEnvironmentToken, setPrefetchEnvironmentToken] = useState(0);
  const failedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const memoryCacheRef = useRef<Map<string, CreatorOptionsCacheRecord>>(new Map());
  const committedRecordRef = useRef<{
    criteriaKey: string;
    record: CreatorOptionsCacheRecord;
  } | null>(null);
  const searchActiveRef = useRef(false);
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchDebounceTimerRef = useRef<number | null>(null);
  const normalizedSearchText = useMemo(
    () => normalizeSearchText(searchText),
    [searchText]
  );

  const departmentKey = args.departmentIds.join(',');
  const teamKey = args.teamIds.join(',');

  useEffect(() => {
    if (!args.enabled || !args.criteriaKey || !args.startDate || !args.endDate) return;

    const criteriaKey = args.criteriaKey;
    const cacheCriteriaKey = `${criteriaKey}|includeTemporary:${args.includeTemporary ? '1' : '0'}`;
    const indexedDbKey = buildCreatorOptionsIndexedDbKey(args.loginId, cacheCriteriaKey);
    const sequence = ++requestSequenceRef.current;
    const controller = new AbortController();
    const forceRevalidate = args.forceRevalidateRef.current;
    args.forceRevalidateRef.current = false;
    failedRef.current = false;
    setLoadFailed(false);
    const memoryRecord = memoryCacheRef.current.get(indexedDbKey) ?? null;
    let latestCompleteRecord: CreatorOptionsCacheRecord | null =
      memoryRecord?.complete !== false ? memoryRecord : null;
    let initialCacheRecordCanBePublished = false;
    let initialCompleteCachePublished = false;

    const isCurrentCriteriaCommitted = () =>
      committedRecordRef.current?.criteriaKey === criteriaKey;

    // 0名キャッシュは必ずサーバーで再検証するため、新しい検索条件では
    // 同期完了前に空結果として公開しない。同一条件の背景再同期だけは、
    // 直前に確定済みの表示を維持する。
    const canPublishBeforeSyncCompletes = (record: CreatorOptionsCacheRecord) =>
      record.complete !== false &&
      (record.options.length > 0 || isCurrentCriteriaCommitted());

    const rememberRecord = (record: CreatorOptionsCacheRecord) => {
      memoryCacheRef.current.set(indexedDbKey, record);
      if (record.complete !== false) {
        latestCompleteRecord = record;
      }
    };

    const publishCompleteRecord = (record: CreatorOptionsCacheRecord): boolean => {
      if (
        requestSequenceRef.current !== sequence ||
        controller.signal.aborted ||
        record.complete === false
      ) {
        return false;
      }

      rememberRecord(record);
      const previous = committedRecordRef.current;
      const optionsChanged =
        previous?.criteriaKey !== criteriaKey ||
        previous.record.options !== record.options;
      committedRecordRef.current = { criteriaKey, record };
      if (optionsChanged) {
        args.setOptions(record.options as WeeklyReportCreatorOption[]);
      }
      args.setResolvedKey(criteriaKey);
      args.setLoadingRemaining(false);
      setAllOptionsLoaded(true);
      setRefreshingCachedOptions(false);
      return true;
    };

    // v2以前のlocalStorage候補は公開設定変更を完全には検知できないため、
    // v3では表示に流用せず削除だけ行う。
    if (args.legacyCacheKey) {
      try {
        localStorage.removeItem(args.legacyCacheKey);
      } catch {
        // localStorageが使えない環境でもページ取得を続ける。
      }
    }

    const applyRecord = (record: CreatorOptionsCacheRecord) => {
      if (requestSequenceRef.current !== sequence || controller.signal.aborted) return;
      rememberRecord(record);

      // 完成済みキャッシュだけは全件を一度に公開し、再検証中も表示を維持する。
      // ネットワークの途中ページと全ページ直後のスナップショットは、差分確認を
      // 含む同期処理が解決するまで画面へ公開しない。
      if (
        initialCacheRecordCanBePublished &&
        !initialCompleteCachePublished &&
        record.complete !== false
      ) {
        initialCompleteCachePublished = true;
        publishCompleteRecord(record);
      }
    };
    const applyInitialRecord = (
      record: CreatorOptionsCacheRecord,
      source: 'cache' | 'network'
    ) => {
      if (requestSequenceRef.current !== sequence || controller.signal.aborted) return;
      initialCacheRecordCanBePublished =
        source === 'cache' && canPublishBeforeSyncCompletes(record);
      setRefreshingCachedOptions(
        source === 'cache' && !isCurrentCriteriaCommitted()
      );
    };
    const applyBroadcastRecord = (record: CreatorOptionsCacheRecord) => {
      if (requestSequenceRef.current !== sequence || controller.signal.aborted) return;
      rememberRecord(record);
      // 別タブが全ページ保存した直後でも、差分確認前の0名レコードはあり得る。
      // 新条件の空結果は、このタブの同期処理が正常終了するまで公開しない。
      if (canPublishBeforeSyncCompletes(record)) {
        publishCompleteRecord(record);
      }
    };
    const applyReset = () => {
      if (requestSequenceRef.current !== sequence || controller.signal.aborted) return;
      memoryCacheRef.current.delete(indexedDbKey);
      latestCompleteRecord = null;
      if (isCurrentCriteriaCommitted()) {
        // 同一条件の再同期では、差分リセット後の再取得中も直前の確定表示を維持する。
        args.setLoadingRemaining(false);
        setAllOptionsLoaded(true);
        setRefreshingCachedOptions(false);
        return;
      }
      args.setOptions([]);
      args.setResolvedKey(criteriaKey);
      args.setLoadingRemaining(true);
      setAllOptionsLoaded(false);
      setRefreshingCachedOptions(false);
    };

    if (memoryRecord && canPublishBeforeSyncCompletes(memoryRecord)) {
      publishCompleteRecord(memoryRecord);
    } else if (!isCurrentCriteriaCommitted()) {
      committedRecordRef.current = null;
      args.setOptions([]);
      args.setResolvedKey(null);
      setAllOptionsLoaded(false);
      args.setLoadingRemaining(true);
    } else {
      // 同一条件の再同期では、直前の完成済み一覧を表示したまま更新する。
      args.setLoadingRemaining(false);
      setAllOptionsLoaded(true);
    }

    const fetchArgs = {
      startDate: args.startDate,
      endDate: args.endDate,
      selectedDepartmentIds: args.departmentIds,
      selectedTeamIds: args.teamIds,
      ...(args.includeTemporary ? { includeTemporary: true } : {}),
      userDep: args.userDep || undefined,
      userTeamId: args.userTeamId,
    };
    const cacheCriteria = createCreatorOptionsCacheCriteria({
      loginId: args.loginId,
      startDate: args.startDate,
      endDate: args.endDate,
      departmentIds: args.departmentIds,
      teamIds: args.teamIds,
      includeTemporary: args.includeTemporary,
      userDep: args.userDep,
      userTeamId: args.userTeamId,
    });
    const unsubscribe = subscribeCreatorOptionsCacheUpdates(
      indexedDbKey,
      (message) => {
        if (message.type === 'reset') {
          applyReset();
          return;
        }
        void readCreatorOptionsCache(indexedDbKey).then((record) => {
          if (record) applyBroadcastRecord(record);
        });
      }
    );

    void withCreatorOptionsFetchLock(indexedDbKey, controller.signal, () =>
      synchronizeCreatorOptionPages({
        key: indexedDbKey,
        criteria: cacheCriteria,
        memoryRecord,
        shouldRevalidate: (record) =>
          forceRevalidate ||
          record.options.length === 0 ||
          record.savedAt < args.latestUpdateMs ||
          Date.now() - record.savedAt >= CREATOR_SYNC_MIN_REVALIDATE_MS,
        fetchPage: async (pageCursor, pageSize) => {
          await waitUntilSearchIdle(searchActiveRef, controller.signal);
          return fetchCreatorPageWithSnapshotFallback(
            { ...fetchArgs, signal: controller.signal },
            pageCursor,
            pageSize
          );
        },
        fetchDelta: (cursor) =>
          fetchWeeklyReportCreatorDelta(
            { ...fetchArgs, signal: controller.signal },
            cursor
          ),
        onRecord: applyRecord,
        onInitialRecord: applyInitialRecord,
        onResetRequired: applyReset,
      })
    )
      .then((record) => {
        if (requestSequenceRef.current !== sequence || controller.signal.aborted) return;
        const complete = publishCompleteRecord(record);
        if (!complete) {
          if (isCurrentCriteriaCommitted()) {
            args.setLoadingRemaining(false);
            setAllOptionsLoaded(true);
          } else {
            args.setLoadingRemaining(true);
            setAllOptionsLoaded(false);
          }
        }
        if (args.legacyCacheKey) {
          try {
            localStorage.removeItem(args.legacyCacheKey);
          } catch {
            // IndexedDB同期済みなので削除失敗は無視する。
          }
        }
      })
      .catch((error) => {
        if (requestSequenceRef.current !== sequence || isAbortError(error)) return;
        failedRef.current = true;
        setRefreshingCachedOptions(false);
        // 完成済みの候補が無いまま失敗した場合は、空一覧(0名確定)と区別して案内する。
        setLoadFailed(!latestCompleteRecord);
        if (latestCompleteRecord) {
          // 完成済みキャッシュの再確認、または全ページ取得後の差分確認だけが
          // 失敗した場合、スナップショットまで未確定へ戻すと取得済み候補や
          // 確定済みの0名結果が消えてしまう。
          // 同期処理が終了するまでは公開していないため、ここで一度だけ確定表示する。
          publishCompleteRecord(latestCompleteRecord);
        } else if (isCurrentCriteriaCommitted()) {
          args.setLoadingRemaining(false);
          setAllOptionsLoaded(true);
        } else {
          args.setLoadingRemaining(false);
          setAllOptionsLoaded(false);
        }
        if (!latestCompleteRecord && !memoryCacheRef.current.has(indexedDbKey)) {
          args.setOptions((current) => current ?? []);
          args.setResolvedKey(criteriaKey);
        }
        // eslint-disable-next-line no-console
        console.error('発行者候補の段階取得に失敗しました', error);
      });

    return () => {
      unsubscribe();
      controller.abort();
    };
    // 配列値は文字列化したキーで比較し、同値配列の再生成では再取得しない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    args.enabled,
    args.criteriaKey,
    args.loginId,
    args.startDate,
    args.endDate,
    departmentKey,
    teamKey,
    args.userDep,
    args.userTeamId,
    args.includeTemporary,
    args.revalidateToken,
    retryToken,
  ]);

  useEffect(() => {
    const handleEnvironmentChange = () => {
      setPrefetchEnvironmentToken((value) => value + 1);
    };
    document.addEventListener('visibilitychange', handleEnvironmentChange);
    window.addEventListener('online', handleEnvironmentChange);
    window.addEventListener('offline', handleEnvironmentChange);
    return () => {
      document.removeEventListener('visibilitychange', handleEnvironmentChange);
      window.removeEventListener('online', handleEnvironmentChange);
      window.removeEventListener('offline', handleEnvironmentChange);
    };
  }, []);

  useEffect(() => {
    if (
      !args.enabled ||
      !allOptionsLoaded ||
      !args.criteriaKey ||
      !args.startDate ||
      !args.endDate ||
      normalizedSearchText ||
      document.visibilityState !== 'visible' ||
      !navigator.onLine ||
      Boolean((navigator as any).connection?.saveData)
    ) {
      return;
    }

    const adjacentRanges = buildAdjacentCreatorRanges(
      args.startDate,
      args.endDate
    );
    if (adjacentRanges.length === 0) return;

    const controller = new AbortController();
    let idleCallbackId: number | null = null;
    const assertPrefetchAllowed = () => {
      if (
        controller.signal.aborted ||
        document.visibilityState !== 'visible' ||
        !navigator.onLine ||
        Boolean((navigator as any).connection?.saveData)
      ) {
        throw new DOMException('Aborted', 'AbortError');
      }
    };

    const runPrefetch = async () => {
      try {
        for (const range of adjacentRanges) {
          assertPrefetchAllowed();
          const adjacentCriteriaKey = replaceCreatorCriteriaDateRange(
            args.criteriaKey as string,
            range.startDate,
            range.endDate
          );
          const adjacentCacheKey = buildCreatorOptionsIndexedDbKey(
            args.loginId,
            `${adjacentCriteriaKey}|includeTemporary:${args.includeTemporary ? '1' : '0'}`
          );
          const prefetchCriteria = createCreatorOptionsCacheCriteria({
            loginId: args.loginId,
            startDate: range.startDate,
            endDate: range.endDate,
            departmentIds: args.departmentIds,
            teamIds: args.teamIds,
            includeTemporary: args.includeTemporary,
            userDep: args.userDep,
            userTeamId: args.userTeamId,
          });
          const existing = await readCreatorOptionsCache(adjacentCacheKey);
          if (
            existing &&
            existing.complete !== false &&
            areCreatorOptionsCacheCriteriaEqual(
              existing.criteria,
              prefetchCriteria
            ) &&
            Date.now() - existing.savedAt < CREATOR_PREFETCH_FRESH_MS
          ) {
            continue;
          }

          const prefetchArgs: WeeklyReportCreatorFetchArgs = {
            startDate: range.startDate,
            endDate: range.endDate,
            selectedDepartmentIds: args.departmentIds,
            selectedTeamIds: args.teamIds,
            ...(args.includeTemporary ? { includeTemporary: true } : {}),
            userDep: args.userDep || undefined,
            userTeamId: args.userTeamId,
            signal: controller.signal,
          };

          await withCreatorOptionsFetchLock(
            adjacentCacheKey,
            controller.signal,
            () =>
              synchronizeCreatorOptionPages({
                key: adjacentCacheKey,
                criteria: prefetchCriteria,
                shouldRevalidate: (record) =>
                  record.complete === false ||
                  record.options.length === 0 ||
                  Date.now() - record.savedAt >= CREATOR_PREFETCH_FRESH_MS,
                fetchPage: (pageCursor, pageSize) => {
                  assertPrefetchAllowed();
                  return fetchCreatorPageWithSnapshotFallback(
                    prefetchArgs,
                    pageCursor,
                    pageSize
                  );
                },
                fetchDelta: (cursor) => {
                  assertPrefetchAllowed();
                  return fetchWeeklyReportCreatorDelta(prefetchArgs, cursor);
                },
                onRecord: () => undefined,
                onInitialRecord: () => undefined,
              })
          );
        }
      } catch (error) {
        if (!isAbortError(error)) {
          // 先読み失敗は現在画面の候補取得へ影響させない。
          // eslint-disable-next-line no-console
          console.debug('発行者候補の先読みに失敗しました', error);
        }
      }
    };

    const timer = window.setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        idleCallbackId = window.requestIdleCallback(() => {
          void runPrefetch();
        }, { timeout: 2000 });
        return;
      }
      void runPrefetch();
    }, CREATOR_PREFETCH_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      if (
        idleCallbackId != null &&
        typeof window.cancelIdleCallback === 'function'
      ) {
        window.cancelIdleCallback(idleCallbackId);
      }
      controller.abort();
    };
    // 配列値は文字列化したキーで比較する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    args.enabled,
    allOptionsLoaded,
    args.criteriaKey,
    args.loginId,
    args.startDate,
    args.endDate,
    departmentKey,
    teamKey,
    args.userDep,
    args.userTeamId,
    args.includeTemporary,
    normalizedSearchText,
    prefetchEnvironmentToken,
  ]);

  useEffect(() => {
    searchAbortRef.current?.abort();
    searchAbortRef.current = null;
    searchActiveRef.current = Boolean(normalizedSearchText);

    if (
      !args.enabled ||
      !args.criteriaKey ||
      !normalizedSearchText ||
      !args.startDate ||
      !args.endDate
    ) {
      setRemoteSearchOptions([]);
      setSearchLoading(false);
      setSearchResultsComplete(true);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearchLoading(true);
    setSearchError(null);

    const search = async () => {
      const merged = new Map<string, WeeklyReportCreatorOption>();
      let pageCursor: string | null = null;
      let resultsComplete = false;

      for (const pageSize of CREATOR_SEARCH_REQUEST_PAGE_SIZES) {
        const page = await fetchWeeklyReportCreatorPage(
          {
            startDate: args.startDate,
            endDate: args.endDate,
            selectedDepartmentIds: args.departmentIds,
            selectedTeamIds: args.teamIds,
            ...(args.includeTemporary ? { includeTemporary: true } : {}),
            userDep: args.userDep || undefined,
            userTeamId: args.userTeamId,
            signal: controller.signal,
          },
          { pageSize, pageCursor, query: searchText }
        );
        if (controller.signal.aborted) return;

        page.items.forEach((option) => {
          const key = String(option.id ?? '').trim().toLocaleLowerCase();
          if (key) merged.set(key, option);
        });
        const nextOptions = Array.from(merged.values()).slice(
          0,
          CREATOR_SEARCH_RESULT_LIMIT
        );
        setRemoteSearchOptions(nextOptions);

        if (!page.hasMore) {
          resultsComplete = true;
          break;
        }
        if (!page.nextPageCursor) {
          resultsComplete = false;
          break;
        }
        pageCursor = page.nextPageCursor;
      }

      if (!controller.signal.aborted) {
        setSearchResultsComplete(resultsComplete);
      }
    };

    void search()
      .catch((error) => {
        if (isAbortError(error)) return;
        setSearchResultsComplete(false);
        setSearchError('発行者の検索に失敗しました。入力し直してください。');
        // eslint-disable-next-line no-console
        console.error('発行者検索に失敗しました', error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setSearchLoading(false);
        if (searchAbortRef.current === controller) {
          searchAbortRef.current = null;
          searchActiveRef.current = false;
        }
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    args.enabled,
    args.criteriaKey,
    args.startDate,
    args.endDate,
    departmentKey,
    teamKey,
    args.userDep,
    args.userTeamId,
    args.includeTemporary,
    searchText,
    normalizedSearchText,
    searchRequest.token,
  ]);

  useEffect(
    () => () => {
      if (searchDebounceTimerRef.current != null) {
        window.clearTimeout(searchDebounceTimerRef.current);
      }
      searchAbortRef.current?.abort();
      searchActiveRef.current = false;
    },
    []
  );

  const onSearchTextChange = useCallback((value: string) => {
    const normalized = normalizeSearchText(value);
    if (searchDebounceTimerRef.current != null) {
      window.clearTimeout(searchDebounceTimerRef.current);
      searchDebounceTimerRef.current = null;
    }
    searchAbortRef.current?.abort();
    searchAbortRef.current = null;
    searchActiveRef.current = Boolean(normalized);
    if (!normalized) {
      setSearchRequest((current) => ({ text: '', token: current.token + 1 }));
      setRemoteSearchOptions([]);
      setSearchLoading(false);
      setSearchResultsComplete(true);
      setSearchError(null);
      return;
    }

    searchDebounceTimerRef.current = window.setTimeout(() => {
      searchDebounceTimerRef.current = null;
      setRemoteSearchOptions([]);
      setSearchResultsComplete(false);
      setSearchError(null);
      setSearchRequest((current) => ({
        text: value,
        token: current.token + 1,
      }));
    }, CREATOR_SEARCH_DEBOUNCE_MS);
  }, []);

  const retry = useCallback(() => {
    if (!failedRef.current) return;
    failedRef.current = false;
    setRetryToken((value) => value + 1);
  }, []);

  return {
    remoteSearchOptions,
    searchLoading,
    searchResultsComplete,
    searchError,
    loadErrorMessage: loadFailed
      ? '発行者の候補を取得できませんでした。メニューを開き直すか、画面を再読み込みしてください。'
      : null,
    allOptionsLoaded,
    loadingText: refreshingCachedOptions
      ? 'キャッシュから表示・最新情報を確認中です…'
      : '残りの発行者を読み込み中です…',
    onSearchTextChange,
    retry,
  };
}
